import { useEffect, useState, useMemo } from 'react';
import { api, formatErr } from '@/lib/api';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const STATE_COLOR = {
  full: { bg: 'bg-emerald-500', label: 'Full day' },
  half: { bg: 'bg-amber-400', label: 'Half day' },
  in: { bg: 'bg-blue-400', label: 'Punched in' },
  leave: { bg: 'bg-primary/80', label: 'Approved leave' },
  absent: { bg: 'bg-red-500/70', label: 'Absent' },
  weekend: { bg: 'bg-muted', label: 'Weekend' },
  future: { bg: 'bg-transparent border border-dashed border-border', label: 'Upcoming' },
};

// weekday index from backend: Monday=0 … Sunday=6
const WEEKDAY = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function AttendanceHeatmap() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/attendance/heatmap', { params: { month } }); setData(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const shift = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [month]);

  return (
    <div className="tactical-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Monthly view</div>
          <div className="font-display text-2xl font-black tracking-tight">Attendance Heatmap · {monthLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="hm-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="hm-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-muted-foreground">
        {Object.entries(STATE_COLOR).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${v.bg}`} />
            <span>{v.label}</span>
          </div>
        ))}
      </div>

      {data && (
        <div className="overflow-x-auto -mx-6 px-6">
          <TooltipProvider delayDuration={100}>
            <table className="border-separate border-spacing-0" data-testid="heatmap-table">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10 text-left text-[10px] uppercase tracking-widest text-muted-foreground pr-4 pb-2 min-w-[160px]">Recruiter</th>
                  {data.days.map(d => (
                    <th key={d.date} className="w-8 text-center pb-2">
                      <div className="text-[10px] text-muted-foreground">{WEEKDAY[d.weekday] || ''}</div>
                      <div className="text-[10px] font-medium">{d.day}</div>
                    </th>
                  ))}
                  <th className="text-center text-[10px] uppercase tracking-widest text-muted-foreground pl-4 pb-2 min-w-[80px]">Present</th>
                  <th className="text-center text-[10px] uppercase tracking-widest text-muted-foreground pl-2 pb-2 min-w-[80px]">Hours</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.user_id} className="hover:bg-muted/40">
                    <td className="sticky left-0 bg-card z-10 text-sm font-medium py-1.5 pr-4 border-t border-border/50">
                      {u.user_name}
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</span>
                    </td>
                    {u.cells.map((c, i) => (
                      <td key={i} className="text-center py-1 border-t border-border/50">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              data-testid={`hm-${u.user_id}-${c.date}`}
                              className={`inline-block w-6 h-6 rounded-sm ${STATE_COLOR[c.state]?.bg || 'bg-muted'} transition-transform hover:scale-125 cursor-pointer`}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            <div className="font-medium">{c.date}</div>
                            <div>{STATE_COLOR[c.state]?.label}</div>
                            {c.hours != null && <div>{c.hours}h logged</div>}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    ))}
                    <td className="text-center text-sm font-display font-black pl-4 border-t border-border/50">{u.totals.full + u.totals.half}</td>
                    <td className="text-center text-sm font-display font-black pl-2 border-t border-border/50">{u.totals.hours}h</td>
                  </tr>
                ))}
                {data.users.length === 0 && (
                  <tr><td colSpan={data.days.length + 3} className="text-center text-muted-foreground py-8">No users to display.</td></tr>
                )}
              </tbody>
            </table>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
